import { OnBoardingWrapper } from '../../../OnBoardingWrapper';
import React, { useEffect, useState } from 'react';
import styles from './styles.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { onBoardingSignUpEffect } from 'store/effects/onBoarding';
import { PENDING } from 'settings/constants/apiState';
import { getOnBoardingData } from 'pages/OnBoarding/selectors';
import { getUserDataSelector } from 'store/selectors/user';
import { Spinner, Button } from 'components';
import { showErrorMessage, formatPhoneNumber } from 'helpers';
import moment from 'moment';
import {
  verifyPhoneNumberWithCode,
  requestPhoneVerificationCode,
  getPhoneVerificationCodeInfo,
} from 'api/user';

type Props = {
  onNext: () => {};
  stageIndex: number;
};

type VerificationCodeData = {
  AllowCodeResendTimestamp: Date;
  ExpirationSeconds: number;
  PhoneNumber: string;
};

export const PhoneVerificationForm: React.FC<Props> = ({ onNext, stageIndex }) => {
  const CODE_LENGTH = 4;
  const { onBoarding } = useSelector(getOnBoardingData);
  const dispatch = useDispatch();
  const user = useSelector(getUserDataSelector);
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [otp, setOtp] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [sending, setSending] = useState<boolean>(false);
  const [resending, setResending] = useState<boolean>(false);
  const [verifying, setVerifying] = useState<boolean>(false);
  const [blocked, setBlocked] = useState<boolean>(false);
  const [remainingSecondsToResendCode, setRemainingSecondsToResendCode] = useState<number | null>(
    null,
  );
  const [verificationCodeData, setVerificationCodeData] = useState<
    undefined | null | VerificationCodeData
  >(undefined);

  const isPending = onBoarding.state === PENDING;

  useEffect(() => {
    if (user.Phones?.length) {
      const primary = user.Phones.find((phone) => phone.IsPrimary);
      if (primary) setPhoneNumber(primary.PhoneNumber);
      else setPhoneNumber(user.Phones[0].PhoneNumber);
      sendOtpCode(primary ? primary.PhoneNumber : user.Phones[0].PhoneNumber);
    } else {
      showErrorMessage('No phone number found.');
    }
  }, [user]);

  useEffect(() => {
    const interval = setInterval(() => {
      const remainingSeconds = moment(verificationCodeData?.AllowCodeResendTimestamp).diff(
        moment(),
        'seconds',
      );
      setRemainingSecondsToResendCode(remainingSeconds);

      if (remainingSeconds < 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [verificationCodeData]);

  const sendOtpCode = async (userPhoneNumber) => {
    setSending(true);
    try {
      const response = await getPhoneVerificationCodeInfo();
      let fetchedVerificationCodeData = response.data;
      if (
        !fetchedVerificationCodeData ||
        fetchedVerificationCodeData?.PhoneNumber !== userPhoneNumber
      ) {
        const requestCodeResponse = await requestPhoneVerificationCode({
          phoneNumber: userPhoneNumber,
        });

        fetchedVerificationCodeData = requestCodeResponse.data;
      }
      setVerificationCodeData(fetchedVerificationCodeData);
    } catch (err: any) {
      showErrorMessage(err);
    }
    setSending(false);
  };

  const onSubmit = () => {
    dispatch(
      onBoardingSignUpEffect(
        {
          IsPhoneVerified: true,
          stageIndex,
        },
        { isCommonInfo: true },
        (err) => {
          if (!err) {
            onNext();
          }
        },
      ),
    );
  };

  const phoneIcon = (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M36.6644 28.1989V33.1989C36.6663 33.663 36.5712 34.1225 36.3853 34.5478C36.1993 34.9731 35.9266 35.3548 35.5846 35.6686C35.2425 35.9824 34.8387 36.2213 34.399 36.37C33.9593 36.5187 33.4934 36.574 33.0311 36.5322C27.9025 35.9749 22.9761 34.2224 18.6478 31.4155C14.6208 28.8566 11.2067 25.4425 8.64777 21.4155C5.83107 17.0675 4.07818 12.1172 3.53111 6.96553C3.48946 6.50464 3.54423 6.04013 3.69194 5.60157C3.83965 5.16301 4.07706 4.76001 4.38905 4.41823C4.70104 4.07645 5.08078 3.80338 5.50409 3.6164C5.9274 3.42942 6.38501 3.33263 6.84777 3.33219H11.8478C12.6566 3.32423 13.4408 3.61066 14.054 4.13808C14.6673 4.6655 15.0679 5.39794 15.1811 6.19886C15.3921 7.79897 15.7835 9.37007 16.3478 10.8822C16.572 11.4787 16.6205 12.127 16.4876 12.7503C16.3547 13.3736 16.0459 13.9457 15.5978 14.3989L13.4811 16.5155C15.8537 20.6881 19.3085 24.1429 23.4811 26.5155L25.5978 24.3989C26.0509 23.9508 26.623 23.6419 27.2463 23.509C27.8696 23.3761 28.5179 23.4246 29.1144 23.6489C30.6266 24.2131 32.1977 24.6045 33.7978 24.8155C34.6074 24.9297 35.3468 25.3375 35.8753 25.9613C36.4039 26.5852 36.6847 27.3815 36.6644 28.1989Z"
        stroke="#04A451"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const handleInputChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < CODE_LENGTH - 1) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  const isOtpValid = (): boolean => {
    return otp.every((digit) => digit.length === 1 && !isNaN(parseInt(digit)));
  };

  const handleContinue = async () => {
    setVerifying(true);
    try {
      const enteredCode = otp.join('');
      const response = await verifyPhoneNumberWithCode({ code: enteredCode });
      if (response.status === 200) onSubmit();
    } catch (err) {
      showErrorMessage('Unable to verify OTP. Please try again.');
    }
    setVerifying(false);
  };

  const resendHandler = async () => {
    setResending(true);
    try {
      const response = await requestPhoneVerificationCode({ phoneNumber });
      setVerificationCodeData(response.data);
    } catch (err: any) {
      if (err.response.data.code === 'MAX_ATTEMPTS_TO_SEND') {
        setBlocked(true);
        showErrorMessage('Maximum attempts to send code reached. Please try again later.');
      }
    }
    setResending(false);
  };

  if (sending) return <Spinner />;

  return (
    <OnBoardingWrapper isPending={isPending} className={styles.wrapper}>
      <div className={styles.content}>
        <div className={styles.icon}>{phoneIcon}</div>
        <div className={styles.heading}>Verify Your Phone Number</div>
        <div className={styles.text}>
          A verification code has been sent to&nbsp;
          <span className={styles.phone}>{formatPhoneNumber(phoneNumber)}</span>.
        </div>
        <div className={styles.text}>
          Please check your message inbox and enter the verification code below.
        </div>
        <div className={styles.box}>
          <div className={styles.otpInput}>
            {otp.map((digit, index) => (
              <input
                key={index}
                id={`otp-${index}`}
                type="text"
                maxLength={1}
                value={digit}
                className={styles.input}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
              />
            ))}
          </div>
          <Button
            className={styles.verifyButton}
            title={'Verify'}
            isPending={isPending || verifying || resending}
            loaderClassName={styles.loader}
            onClick={handleContinue}
            disabled={
              !isOtpValid() ||
              isPending ||
              verifying ||
              resending ||
              !verificationCodeData ||
              blocked
            }
          />
        </div>
        <div className={styles.resendText}>
          Didn’t receive it?&nbsp;
          {remainingSecondsToResendCode !== null && remainingSecondsToResendCode > 0 ? (
            <span>{`Resend the code in ${remainingSecondsToResendCode} seconds`}</span>
          ) : null}
          {!blocked && (!remainingSecondsToResendCode || remainingSecondsToResendCode <= 0) ? (
            <span className={styles.resend} onClick={resendHandler}>
              Resend
            </span>
          ) : null}
        </div>
      </div>
    </OnBoardingWrapper>
  );
};
